import React, { useContext, useEffect, useState } from "react";
import { ContentCopy, FiberManualRecord } from "@mui/icons-material";
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import { RoomContext } from "App/context";
import Paper from "common/components/Paper";
import PageHeader from "common/components/PageHeader";
import { useSocket } from "common/hooks/useSocket";
import {
  GameMode,
  GameModeDictionary,
  PlayersNumber,
  PlayersNumberDictionary,
  Role,
  RoomParams,
  RoomState,
} from "types/Room/type";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GenshinButton from "common/components/GenshinButton";
import { Player } from "types/Player/type";
import { isUserAdmin } from "common/utils";
import Typography from "common/components/Typography";

type CreateJoinRoomProps = {};

const CreateJoinRoom = (props: CreateJoinRoomProps) => {
  const { dispatch, localPlayer, availableSheets } = useContext(RoomContext);
  const { t } = useTranslation();
  const socket = useSocket();
  const navigate = useNavigate();
  const [roomName, setRoomName] = useState<string>("");
  const [roomsList, setRoomsList] = useState<RoomState[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<number>();
  const [selectedPlayersNumber, setSelectedPlayersNumber] =
    useState<PlayersNumber>(PlayersNumber._1vs1);
  const [selectedGameMode, setSelectedGameMode] = useState<GameMode>(
    GameMode.SpiralAbyss,
  );
  const [privateRoom, setPrivateRoom] = useState<boolean>(false);
  const [solo, setSolo] = useState<boolean>(false);
  const isLocalPlayerSetup =
    localPlayer?.pseudo !== undefined &&
    localPlayer?.characters !== undefined &&
    localPlayer?.characters.length > 0;
  const isAdmin = isUserAdmin(localPlayer);

  const subscribeEvents = () => {
    socket.on("update-role", (role: Role) => dispatch({ role }));
    socket.on("start-draft", () => navigate("/draft"));
    socket.on("join-room", (admin?: boolean) => {
      if (admin) dispatch({ admin: true });
      navigate("/waitingRoom");
    });
    socket.on("rooms-list", (rooms: RoomState[]) => {
      setRoomsList(rooms);
    });
  };

  const renderRoomState = (state: RoomState["state"]) => {
    switch (state) {
      case "InWaitingRoom":
        return (
          <>
            <FiberManualRecord color="success" />
            {t("Préparation")}
          </>
        );
      case "InDraft":
        return (
          <>
            <FiberManualRecord color="warning" />
            {t("En draft")}
          </>
        );
      case "EndDraft":
        return (
          <>
            <FiberManualRecord color="error" />
            {t("Terminé")}
          </>
        );
    }
  };

  const handleJoinRoom = (name?: string, solo?: boolean) => {
    const params: RoomParams = {
      name: name ?? roomName,
      privateRoom: solo || privateRoom,
      playersNumber: selectedPlayersNumber,
      ...(solo ? { solo: true } : {}),
      sheet: {
        id: selectedSheet ?? 1,
        name: availableSheets.find((s) => s.id === selectedSheet)?.name ?? "",
      },
      gameMode: selectedGameMode,
    };
    const player: Player = {
      id: localPlayer?.id,
      pseudo: localPlayer?.pseudo,
      box: localPlayer?.characters,
      avatar: localPlayer?.avatar,
      preferences: localPlayer?.preferences ?? [],
    };
    dispatch({ params });
    socket.emit("send-join-room", params, player);
  };

  useEffect(() => {
    if (availableSheets.length > 0) setSelectedSheet(availableSheets[0].id);
  }, [availableSheets]);

  useEffect(() => {
    subscribeEvents();
    socket.connected && socket.emit("get-rooms");

    return () => {
      socket.removeListener("update-role");
      socket.removeListener("start-draft");
      socket.removeListener("join-room");
      socket.removeListener("rooms-list");
    };
  }, []);

  return (
    <Paper>
      <Stack gap={2} padding={1}>
        <PageHeader label={t("Créer ou rejoindre un salon")} />
        <Stack gap={1} direction={{ xs: "column", sm: "row" }} width="100%">
          <Stack gap={1} flex={1} minWidth={0} direction="column">
            <Tabs
              value={solo ? "solo" : "multi"}
              onChange={(_, value) => setSolo(value === "solo")}
              variant="fullWidth"
            >
              <Tab value="multi" label={t("Multijoueur")} />
              <Tab value="solo" label={t("Solo")} />
            </Tabs>
            {!solo && (
              <FormControl size="small" required fullWidth>
                <InputLabel>{t("Nom du salon")}</InputLabel>
                <OutlinedInput
                  fullWidth
                  required
                  value={roomName}
                  onChange={(event) => setRoomName(event.target.value)}
                  label={t("Nom du salon")}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={async () =>
                          await navigator.clipboard.writeText(roomName)
                        }
                        size="small"
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            )}
            <Paper
              sx={{
                bgcolor: ({ palette }) => palette.background.highlight,
              }}
            >
              <Stack gap={1}>
                <Stack px={1}>{t("Paramètres")}</Stack>
                <Divider orientation="horizontal" />
                <Stack gap={2} mt={1}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t("Mode de jeu")}</InputLabel>
                    <Select
                      label={t("Mode de jeu")}
                      value={selectedGameMode}
                      onChange={(evt) =>
                        setSelectedGameMode(evt.target.value as GameMode)
                      }
                    >
                      {Object.values(GameMode).map((n, i) => (
                        <MenuItem value={n} key={i}>
                          {t(GameModeDictionary[n])}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t("Feuille d'équilibrage")}</InputLabel>
                    <Select
                      label={t("Feuille d'équilibrage")}
                      value={
                        availableSheets.length > 0 && selectedSheet
                          ? selectedSheet
                          : ""
                      }
                      onChange={(evt) =>
                        setSelectedSheet(evt.target.value as number)
                      }
                    >
                      {availableSheets.map((s) => (
                        <MenuItem value={s.id} key={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Stack direction="row" gap={1}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>{t("Nombre de joueurs")}</InputLabel>
                      <Select
                        label={t("Nombre de joueurs")}
                        value={selectedPlayersNumber}
                        onChange={(evt) =>
                          setSelectedPlayersNumber(
                            evt.target.value as PlayersNumber,
                          )
                        }
                      >
                        {Object.values(PlayersNumber).map((n, i) => (
                          <MenuItem value={n} key={i}>
                            {PlayersNumberDictionary[n]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {/* Le solo est toujours privé : la case n'existe qu'en multijoueur. */}
                    {!solo && (
                      <>
                        <Divider orientation="vertical" flexItem />
                        <FormControlLabel
                          control={
                            <Checkbox
                              onChange={(evt) =>
                                setPrivateRoom(evt.target.checked)
                              }
                              checked={privateRoom}
                              size="small"
                            />
                          }
                          label={t("Privé")}
                        />
                      </>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
            <GenshinButton
              fullWidth
              disabled={
                (!solo && roomName === "") ||
                selectedSheet === undefined ||
                !isLocalPlayerSetup
              }
              variant="contained"
              sx={{ mt: "auto" }}
              onClick={() =>
                solo
                  ? // Room privée au nom unique : le serveur la cache du lobby et la ferme aux autres.
                    handleJoinRoom(
                      `solo-${localPlayer?.id}-${Date.now().toString(36)}`,
                      true,
                    )
                  : handleJoinRoom()
              }
            >
              {solo ? t("Commencer") : t("Créer")}
            </GenshinButton>
          </Stack>
          <Divider orientation="vertical" flexItem />
          <Paper
            sx={{
              flex: 1,
              minWidth: 0,
              width: "100%",
              bgcolor: ({ palette }) => palette.background.highlight,
            }}
          >
            <Stack height="18rem" gap={1}>
              <Typography size="sm" px={1}>
                {t("Salons ({{count}})", { count: roomsList.length })}
              </Typography>
              <Divider orientation="horizontal" />
              <Stack overflow="auto" gap={1}>
                {roomsList.map(({ name, state, privateRoom }, index) => (
                  <MenuItem
                    key={index}
                    disabled={!isLocalPlayerSetup || (!isAdmin && privateRoom)}
                    onClick={() => handleJoinRoom(name)}
                    sx={{
                      borderRadius: 1,
                      bgcolor: ({ palette }) => palette.background.highlight,
                      py: 0.5,
                    }}
                  >
                    <Stack
                      width="100%"
                      justifyContent="space-between"
                      direction="row"
                      gap={1}
                    >
                      <Tooltip
                        title={privateRoom ? t("Salon privé") : name}
                        arrow
                      >
                        <Stack
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          display="block"
                        >
                          {privateRoom ? <i>{t("Salon privé")}</i> : name}
                        </Stack>
                      </Tooltip>
                      <Stack direction="row" alignItems="center">
                        {renderRoomState(state)}
                      </Stack>
                    </Stack>
                  </MenuItem>
                ))}
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default CreateJoinRoom;
